import React, { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useNavigate } from "react-router";
import { Box, Button, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import defer from "lodash/defer";
import { enqueueSnackbar } from "notistack";

import { LoadingButton } from "ui";

import analytics from "fond/analytics";
import { selectCurrentAccount } from "fond/api/accountSlice";
import { selectFoldersByParentId, selectFoldersForMarketLensAdd } from "fond/api/folderSlice";
import { useCreateMultiProjectMutation } from "fond/api/multiProjectsSlice";
import { getDefaultAssessConfiguration } from "fond/cityPlanner/assessConfigurationHelper";
import { HOME_FOLDER_ID } from "fond/constants";
import { TextField } from "fond/form/fields";
import { FolderPickerField } from "fond/form/fields/FolderPickerField";
import { TextFieldValue } from "fond/form/fields/TextField";
import { Folder, HomeFolder, HTTPErrorResponse, Maybe } from "fond/types";
import { setValue } from "fond/utils/formMutators";
import { useAppSelector } from "fond/utils/hooks";
import { generateUnusedName } from "fond/utils/naming";
import { required } from "fond/utils/validation";
import { Message, Modal } from "fond/widgets";

import { Chip, Container, FormRow, Wrapper } from "./NewCityPlanner.styles";

const dotPoints = [
  { title: "Area of interest", description: "Define your area of interest, draw it on the map or upload your own data." },
  { title: "Assess", description: "Explore and assess the area to filter down your target market." },
  { title: "Create scenarios", description: "Generate scenarios based on your objectives, costings, and revenue goals." },
] as const;

interface IFormData {
  Name: string;
  FolderId: string | null;
  Description: string | null;
}

interface IProps {
  /**
   * The parent folder
   */
  parentFolder?: Folder;
  /**
   * Callback function to handle the onClose of the modal
   */
  onClose(): void;
}

const NewCityPlanner: React.FC<IProps> = ({ parentFolder, onClose }: IProps) => {
  const navigate = useNavigate();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [selectedFolderId, setSelectedFolderId] = useState<Maybe<string>>(parentFolder?.ID ?? HOME_FOLDER_ID);
  const siblings = useAppSelector((state) =>
    selectFoldersByParentId(state, selectedFolderId === HOME_FOLDER_ID || !selectedFolderId ? undefined : selectedFolderId)
  );
  const folders = useAppSelector((state) => selectFoldersForMarketLensAdd(state, currentAccount));

  const onInputRefReceived = (el: HTMLInputElement) => {
    if (el) {
      // defer selection to give the input time to render
      // and the selection to take place in the next cycle
      defer(() => el.select());
    }
  };

  // Creating a Market Lens project creates a folder having the same name, so suggest a default name that doesn't
  // overlap with any existing one.
  const defaultName = useMemo(
    () =>
      generateUnusedName(
        "Untitled Market Lens project",
        siblings.map((f) => f.Name)
      ),
    [siblings]
  );

  const [createCityPlannerProject, { isLoading: isSaving, isError, error }] = useCreateMultiProjectMutation();

  /**
   * On submit function called when the form is submitted and valid
   */
  const handleOnSubmit = async ({ Name, Description, FolderId }: IFormData) => {
    if (!currentAccount) {
      enqueueSnackbar("There was an issue creating this project. Could not get account context.", { variant: "error" });
      return;
    }

    createCityPlannerProject({
      FolderID: FolderId === HOME_FOLDER_ID ? null : FolderId,
      AccountID: currentAccount?.ID,
      Name,
      Description,
      AssessConfiguration: getDefaultAssessConfiguration(),
    })
      .unwrap()
      .then((multiProject) => {
        onClose();
        analytics.track("project.market-lens.created");
        // Navigate to the MultiProject
        navigate(`/city/${multiProject.ID}`);
      })
      .catch((e) => {
        Sentry.captureException(e);
      });
  };

  const handleChangeFolder = (event: React.SyntheticEvent, option: Maybe<Folder | HomeFolder>) => {
    setSelectedFolderId(option?.ID);
  };

  /**
   * Validates that the Name is currently not in use
   * @param value New Name value
   */
  const uniqueName = (value: TextFieldValue) => {
    const newName = value?.toString().toLowerCase().trim();
    const existingFolder = siblings.some((folder: Folder) => folder.Name.toLowerCase().trim() === newName);
    if (existingFolder || newName === "home") {
      return "This name is already in use. Please use another.";
    }
    return undefined;
  };

  const formValidator = (values: IFormData) => {
    return {
      Name: required(values.Name) || uniqueName(values.Name),
    };
  };

  let errorMessage = "There was an issue creating the Market Lens project. Please try again.";
  if (error && "data" in error && !("error" in error)) {
    const errorData = error.data as HTTPErrorResponse;
    if (errorData.code === "UniquenessViolation") {
      errorMessage = "The project could not be created as this name is already in use. Please use another.";
    }
  }

  const getChipComponent = (title: string, index: number, description: string) => {
    return (
      <Box display="flex" flexDirection="column" mb={2} key={title}>
        <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
          <Chip color="primary" label={index} />
          <Box>
            <Typography fontWeight={600}>{title}</Typography>
          </Box>
        </Box>
        <Typography variant="subtle" ml={3.5}>
          {description}
        </Typography>
      </Box>
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      data-testid="new-city-planner-modal"
      PaperProps={{
        style: {
          width: "80vw",
          minHeight: "50vh",
          maxHeight: "80vh",
          maxWidth: "1200px",
          padding: "3rem",
        },
      }}
      DialogContentProps={{ sx: { alignContent: "center" } }}
      DialogActionsProps={{ sx: { padding: "3rem 4rem" } }}
      variant="secondary"
      content={
        <Container>
          <Typography variant="h1" mb={2}>
            Your market knowledge starts here.
          </Typography>
          <Typography variant="h3" mb={6}>
            Let's begin.
          </Typography>

          <Wrapper>
            <Box flex={1}>
              <Form<IFormData>
                initialValues={{
                  Name: defaultName,
                  FolderId: parentFolder?.ID ?? HOME_FOLDER_ID,
                  Description: null,
                }}
                keepDirtyOnReinitialize
                onSubmit={handleOnSubmit}
                validate={formValidator}
                render={({ handleSubmit }) => {
                  return (
                    <form onSubmit={handleSubmit} id="new-city-planner-form">
                      <FormRow>
                        <TextField
                          name="Name"
                          required
                          label="Project name"
                          autoFocus
                          autoComplete="off"
                          inputProps={{ ref: onInputRefReceived }}
                          fullWidth
                        />
                      </FormRow>
                      <FormRow>
                        <FolderPickerField name="FolderId" folders={folders} account={currentAccount!} compact onChange={handleChangeFolder} />
                      </FormRow>
                      <FormRow>
                        <TextField name="Description" label="Description" fullWidth multiline rows={3} />
                      </FormRow>
                    </form>
                  );
                }}
                mutators={{ setValue }}
              />
              <Box maxWidth={380} display="flex" alignItems="center" flexDirection="column" gap={1}>
                {isError && (
                  <Box mt={2}>
                    <Message type="error">{errorMessage}</Message>
                  </Box>
                )}
                <LoadingButton
                  data-testid="new-city-planner-project-save-button"
                  color="primary"
                  loading={isSaving}
                  type="submit"
                  form="new-city-planner-form"
                  fullWidth
                  sx={{ mt: 4 }}
                >
                  Get Started
                </LoadingButton>
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" gap={2} flex={1} justifyContent="center">
              {dotPoints.map((point, index) => getChipComponent(point.title, index + 1, point.description))}
            </Box>
          </Wrapper>
        </Container>
      }
      actions={
        <Button data-testid="new-city-planner-project-cancel-button" color="primary" onClick={onClose}>
          Cancel
        </Button>
      }
    />
  );
};

export default NewCityPlanner;
